<template>
  <v-layout
    row
    wrap
    class="mb-2"
  >
    <v-flex
      v-if="!expenses.length"
      xs12
      md6
      offset-md3
    >
      <v-card
        flat
        color="transparent"
      >
        <v-card-title class="title justify-center">
          <div>Getting started with Expenses</div>
        </v-card-title>

        <v-card-text class="text-justify">
          <p>
            Expenses represent costs that your project is going to incur.
            Each expense has an <em>expected cost</em>, <em>expected date</em>,
            and eventually a currency (only PRO plan and above) and optional fields like <em>type</em>.
          </p>

          <p>
            Atlas automatically calculates several standard figures like
            <help-abbr full>
              EAC
            </help-abbr>.
          </p>
        </v-card-text>

        <v-card-actions class="justify-center">
          <v-btn
            color="primary"
            @click="showExpenseDialog(true)"
          >
            {{ $t('create') + ' ' + $t('expenses._') }}
          </v-btn>

          <open-help-btn />
        </v-card-actions>
      </v-card>
    </v-flex>

    <v-flex
      v-if="expenses.length"
      xs12
    >
      <v-card>
        <v-card-title>
          {{ $t('expenses._') }}
          {{ totalExpenses | fmtCurrency }}

          <v-spacer />

          <v-btn
            text
            color="primary"
            @click="showExpenseDialog(true)"
          >
            <v-icon>add</v-icon>
            {{ $t('create') }}
          </v-btn>

          <v-text-field
            v-model="expensesSearch"
            :label="$t('search')"
            prepend-inner-icon="search"
            outlined
            dense
            single-line
            hide-details
            clearable
            class="ml-2"
          />
        </v-card-title>

        <expenses-types
          v-if="false"
          v-model="typeFilter"
          :data="totalExpensesByType"
        />

        <v-layout
          row
          wrap
        >
          <v-flex
            xs12
            md6
            lg8
          >
            <v-data-table
              :headers="expensesHeaders"
              :items="filteredExpenses.models"
              :search="expensesSearch"
              item-key="id"
              class="expenses"
            >
              <!-- <expense-item
                  :props="props"
                  :active-row="activeRow"
                  @select="expenseId => activeRow = expenseId"
                  @edit-expense="editExpense"
                  @show-actuals-dialog="showActualsDialog">
                </expense-item> -->
              <template v-slot:item.cost="{ item }">
                <span class="text-no-wrap">
                  {{ item.$.cost | fmtCurrency }}
                </span>
              </template>

              <template v-slot:item.date="{ item }">
                <span class="text-no-wrap">
                  {{ item.$.date | fmtDate }}
                </span>
              </template>

              <template v-slot:item.action="{ item }">
                <span class="text-no-wrap">
                  <v-icon
                    small
                    class="mr-2"
                    @click="editExpense(item)"
                  >
                    edit
                  </v-icon>

                  <v-icon
                    small
                    @click="deleteExpense(item)"
                  >
                    delete
                  </v-icon>
                </span>
              </template>

              <template
                v-if="props.item.$.actuals"
                slot="expand"
                slot-scope="props"
              >
                <v-list
                  dense
                  class="pa-0 grey lighten-4"
                >
                  <template v-for="(actual, actualKey) in props.item.$.actuals">
                    <v-list-item :key="`actual-${actualKey}`">
                      <v-list-item-content>
                        <v-list-item-subtitle>
                          {{ actual.amount | fmtCurrency }}
                          <span
                            v-if="actual.date"
                          > {{ actual.date.toDate() | fmtDate }}</span>
                        </v-list-item-subtitle>
                      </v-list-item-content>

                      <v-list-item-avatar>
                        <v-btn
                          icon
                          @click="editActual(props.item, actualKey, actual)"
                        >
                          <v-icon>edit</v-icon>
                        </v-btn>
                      </v-list-item-avatar>
                      <v-list-item-avatar>
                        <v-btn
                          icon
                          @click="deleteActual(props.item, actualKey)"
                        >
                          <v-icon class="red--text">
                            delete
                          </v-icon>
                        </v-btn>
                      </v-list-item-avatar>
                    </v-list-item>

                    <v-divider :key="'d-' + actualKey" />
                  </template>
                </v-list>
              </template>
            </v-data-table>
          </v-flex>

          <v-flex
            xs12
            md6
            lg4
          >
            <comments
              v-if="activeRow"
              :doc-ref="activeRow"
              :project="projectId"
            />

            <div
              v-else
              class="title grey--text text--lighten-1 font-weight-light text-center"
            >
              Select an Expense for details
            </div>
          </v-flex>
        </v-layout>
      </v-card>
    </v-flex>

    <v-flex
      v-if="expenses.length"
      xs12
    >
      <expenses-chart :data="filteredExpenses.models" />
    </v-flex>

    <dialog-actual-form
      v-model="actualsDialogActual"
      :expense="actualsDialogExpense"
      :show.sync="showActualsDialogFlag"
    />

    <dialog-expense-form
      v-model="expenseToEdit"
      :project="projectId"
      :show.sync="showNewExpenseDialog"
      @change-cost="onChangeCost"
    />
  </v-layout>
</template>

<script>
import { mapGetters } from 'vuex'

import ProjectComponent from '@/mixins/ProjectComponent'
import Expense from '@/models/Expense'
import ExpensesCollection from '@/models/ExpensesCollection'
import Comments from '@/components/activities/Comments'

import DialogExpenseForm from './DialogExpenseForm'
import DialogActualForm from './DialogActualForm'
import ExpensesChart from './ExpensesChart'
import ExpensesTypes from './ExpensesTypes'

export default {
  name: 'ProjectExpenses',
  components: {
    Comments,
    DialogExpenseForm,
    DialogActualForm,
    ExpensesChart,
    ExpensesTypes
  },
  mixins: [ ProjectComponent ],
  data () {
    return {
      projectId: this.$route.params.name,
      expenses: new ExpensesCollection(),
      totalExpenses: 0,
      totalExpensesActuals: 0,
      totalExpensesByType: {},

      openMenu: false,
      activeRow: null,
      expensesSearch: null,
      expensesHeaders: [
        {
          text: 'Name',
          align: 'left',
          sortable: true,
          value: '$.name'
        },
        {
          text: 'Cost',
          align: 'right',
          sortable: true,
          value: 'cost'
        },
        {
          text: 'Date',
          align: 'right',
          sortable: true,
          value: 'date'
        }
      ],
      totalResources: 0,
      showActualsDialogFlag: false,
      actualsDialogExpense: undefined,
      actualsDialogActual: {},
      totalResourcesActuals: 0,
      showNewExpenseDialog: false,
      expenseToEdit: undefined,
      typeFilter: []
    }
  },
  computed: {
    ...mapGetters('organizations', [ 'customProperties' ]),
    formulaScope () {
      return {
        expenses: this.expenses
      }
    },
    filteredExpenses () {
      return this.expenses.filter((expense) => {
        if (this.typeFilter.length) {
          return this.typeFilter.includes(expense.type)
        } else {
          return true
        }
      })
    }
  },
  mounted () {
    for (const prop of this.customProperties('expense')) {
      this.expensesHeaders.push({
        text: prop.name,
        align: 'left',
        sortable: true,
        value: `$.${prop.key}`
      })
    }

    // add the action buttons as last column
    this.expensesHeaders.push({
      text: '',
      sortable: false,
      value: 'action'
    })
  },
  methods: {
    editExpense (expense) {
      this.showExpenseDialog(true, expense)
    },
    deleteExpense (expense) {
      expense.delete()
    },
    showExpenseDialog (show, expense) {
      this.showNewExpenseDialog = show
      this.expenseToEdit = expense ||
        new Expense({ project: this.projectId }, this.expenses)
    },
    editActual (expense, actualKey, actual) {
      this.showActualsDialog(expense, actual)
    },
    deleteActual (expense, actualKey) {
      expense.actuals.splice(actualKey, 1)
      expense.save()
    },
    showActualsDialog (expense, actual) {
      this.showActualsDialogFlag = !!expense
      this.actualsDialogExpense = expense

      if (expense && !actual) {
        actual = {}
        expense.actuals.push(actual)
      }
      this.actualsDialogActual = actual
    },
    onChangeCost () {
      this.updateTotals()

      this.project.estimateAtCompletion = this.totalExpenses
      this.project.save()
    },
    updateTotals () {
      this.totalExpenses = 0
      this.totalExpensesActuals = 0
      this.totalExpensesByType = 0

      let x = {}
      this.expenses.models.forEach(expense => {
        this.totalExpenses += expense.$.cost
        this.totalExpensesActuals += expense.$.actualCost

        const type = expense.$.type || 'Others'
        x[type] = (x[type] || 0) + (expense.$.cost || 0)
      })
      this.totalExpensesByType = x
    },
    async loadData () {
      this.expenses = new ExpensesCollection()
      await this.expenses.fetch({
        project: this.projectId
      })

      this.updateTotals()
    }
  }
}
</script>

<style lang="scss">
.expenses {
  border-right: 1px solid rgba(0,0,0,.12);
}
</style>
